/**
 * BannerRenderer — Renders the consent banner into an isolated Shadow DOM.
 *
 * Uses Shadow DOM to prevent host page CSS from leaking in/out.
 * Supports: bottom_bar, top_bar, bottom_left_popup, bottom_right_popup,
 *           centered_modal, full_screen
 */

import type { BannerConfig } from "./types";
import type { ConsentDecisions } from "../core/StorageAdapter";
import type { GeoResult } from "../core/GeoDetector";
import type { VendorList, Vendor } from "../tcf/TcfTypes";
import { defaultTranslations, resolveTranslation, RTL_LANGUAGES, type TranslationStrings } from "../i18n/translations";
import { readableTextColor, mutedColor } from "./color";

export type BannerLayout =
  | "bottom_bar"
  | "top_bar"
  | "bottom_left_popup"
  | "bottom_right_popup"
  | "centered_modal"
  | "full_screen";

export interface ConsentMeta {
  timestamp: number;
  sessionId: string;
}

type DecisionCallback = (decisions: ConsentDecisions) => void;

/**
 * Strip characters that could break out of a CSS declaration or the <style>
 * element (so a tenant colour/font value cannot inject rules or markup).
 */
export function sanitizeCssValue(value: string): string {
  return String(value ?? "").replace(/[<>{};\\"']/g, "");
}

/** Escape a string for safe interpolation into HTML text or an attribute. */
export function escapeHtmlValue(str: string): string {
  return String(str ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export class BannerRenderer {
  private host: HTMLElement | null = null;
  private shadow: ShadowRoot | null = null;
  private config: BannerConfig;
  private geo: GeoResult;
  private onDecision: DecisionCallback;
  private lang: string;
  private initialDecisions: ConsentDecisions | null;
  private vendorList: VendorList | null;
  private consentMeta: ConsentMeta | null;
  private onDismiss: (() => void) | null;
  private previouslyFocused: HTMLElement | null = null;

  constructor(config: BannerConfig, geo: GeoResult, onDecision: DecisionCallback, lang: string = "en", initialDecisions: ConsentDecisions | null = null, vendorList: VendorList | null = null, consentMeta: ConsentMeta | null = null, onDismiss: (() => void) | null = null) {
    // Work on a shallow copy and escape tenant-controlled custom copy up front,
    // so nothing the customer configures can inject markup into the shadow DOM
    // (stored XSS via banner_configs). CSS-context values are sanitized in
    // render(); logo_url is escaped at its use site.
    this.config = { ...config };
    if (config.custom_copy && typeof config.custom_copy === "object") {
      const escaped: Record<string, Record<string, string>> = {};
      for (const [section, vals] of Object.entries(config.custom_copy)) {
        if (vals && typeof vals === "object") {
          escaped[section] = {};
          for (const [k, v] of Object.entries(vals)) {
            escaped[section][k] = typeof v === "string" ? this.escapeHtml(v) : v;
          }
        }
      }
      this.config.custom_copy = escaped;
    }
    this.geo = geo;
    this.onDecision = onDecision;
    this.lang = lang;
    this.initialDecisions = initialDecisions;
    this.vendorList = vendorList;
    this.consentMeta = consentMeta;
    this.onDismiss = onDismiss;
  }

  show(): void {
    if (this.host) return; // Already shown
    // Another renderer instance may already be on screen (e.g. the preference
    // center re-opened while a banner is visible) — never stack a second host.
    if (document.getElementById("pb-consent-host")) return;

    if (!document.body) {
      document.addEventListener("DOMContentLoaded", () => this.show(), { once: true });
      return;
    }

    this.previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.host = document.createElement("div");
    this.host.id = "pb-consent-host";
    this.host.style.cssText = "position: fixed; z-index: 2147483647; font-size: 16px;";
    document.body.appendChild(this.host);

    this.shadow = this.host.attachShadow({ mode: "closed" });
    this.render();
    // Focus first interactive button for keyboard users
    requestAnimationFrame(() => {
      const firstBtn = this.shadow?.getElementById("pb-accept-all") as HTMLElement | null;
      firstBtn?.focus();
    });
  }

  hide(): void {
    this.host?.remove();
    this.host = null;
    this.shadow = null;
    if (this.previouslyFocused?.isConnected) {
      try { this.previouslyFocused.focus({ preventScroll: true }); } catch { /* ignore */ }
    }
    this.previouslyFocused = null;
  }

  private render(): void {
    if (!this.shadow) return;

    const { config } = this;
    // Sanitize style-context values so a tenant colour/font cannot break out of
    // the <style> block. Idempotent, so repeated renders stay safe.
    config.primary_color = sanitizeCssValue(config.primary_color);
    config.background_color = sanitizeCssValue(config.background_color);
    config.text_color = sanitizeCssValue(config.text_color);
    config.button_border_radius = sanitizeCssValue(config.button_border_radius);
    if (config.font_family) config.font_family = sanitizeCssValue(config.font_family);
    const copy = this.getCopy();
    const layout = config.layout as BannerLayout;

    // WCAG-safe derived colors: button text is computed from the tenant's
    // primary luminance (white was hardcoded and fails AA on the default
    // #6366f1), and "muted" text is a solid blend that never drops below the
    // required contrast (the old opacity: 0.4 landed near 2:1).
    const acceptText = readableTextColor(config.primary_color);
    const muted = mutedColor(config.text_color, config.background_color, 4.5);
    const mutedBorder = mutedColor(config.text_color, config.background_color, 3.0);

    const positionStyles = this.getPositionStyles(layout);

    const isRtl = RTL_LANGUAGES.has(this.lang);

    this.shadow.innerHTML = `
      <style>
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :host { font-family: ${config.font_family || "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"}; }
        .pb-container {
          direction: ${isRtl ? "rtl" : "ltr"};
          position: fixed;
          ${positionStyles}
          background: ${config.background_color};
          color: ${config.text_color};
          border-radius: ${layout.includes("popup") || layout === "centered_modal" ? "12px" : "0"};
          box-shadow: 0 4px 24px rgba(0,0,0,0.12);
          padding: 20px 24px;
          max-width: ${layout === "bottom_bar" || layout === "top_bar" ? "100%" : "480px"};
          width: ${layout === "bottom_bar" || layout === "top_bar" ? "100%" : "min(480px, calc(100vw - 32px))"};
          max-height: min(85vh, calc(100dvh - 32px));
          overflow-y: auto;
          animation: pb-slide-in 0.3s ease-out;
        }
        @media (prefers-reduced-motion: reduce) {
          .pb-container { animation: none; }
        }
        @media (max-width: 600px) {
          .pb-container { width: calc(100vw - 20px); max-width: none; padding: 16px; }
          .pb-actions { flex-direction: column; align-items: stretch; }
          .pb-actions .pb-btn { width: 100%; }
        }
        .pb-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: -1;
          display: ${layout === "centered_modal" || layout === "full_screen" ? "block" : "none"};
        }
        @keyframes pb-slide-in {
          from { opacity: 0; transform: translateY(${layout === "top_bar" ? "-20px" : "20px"}); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pb-header { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
        .pb-logo { height: 28px; width: auto; }
        .pb-title { font-size: 15px; font-weight: 700; color: ${config.text_color}; }
        .pb-body { font-size: 13px; line-height: 1.6; color: ${config.text_color}; opacity: 0.85; margin-bottom: 16px; }
        .pb-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
        .pb-btn {
          display: inline-flex; align-items: center; justify-content: center;
          padding: 9px 18px; font-size: 13px; font-weight: 600; cursor: pointer;
          border: none; border-radius: ${config.button_border_radius};
          transition: opacity 0.15s, transform 0.1s;
          min-width: 100px;
        }
        .pb-btn:hover { opacity: 0.9; transform: translateY(-1px); }
        .pb-btn:active { transform: translateY(0); }
        .pb-btn:focus-visible { outline: 2px solid ${config.primary_color}; outline-offset: 2px; }
        .pb-toggle:focus-visible { outline: 2px solid ${config.primary_color}; outline-offset: 2px; }
        .pb-btn-accept {
          background: ${config.primary_color}; color: ${acceptText};
        }
        /* Reject carries the same visual weight as Accept (equal size via
           .pb-btn, solid full-strength border ≥3:1 wherever body text is
           legible) so consent is freely given, not nudged. */
        .pb-btn-reject {
          background: ${config.background_color}; color: ${config.text_color};
          border: 2px solid ${config.text_color};
        }
        /* Manage is deliberately the least prominent action: a text link. */
        .pb-btn-manage {
          background: none; border: none;
          color: ${config.primary_color};
          text-decoration: underline; text-underline-offset: 2px;
          min-width: 0; padding: 9px 4px;
        }
        /* While the preferences panel is open, Manage becomes the primary
           "Save Preferences" action and regains button weight. */
        .pb-btn-manage.pb-btn-save {
          background: ${config.primary_color}; color: ${acceptText};
          text-decoration: none; min-width: 100px; padding: 9px 18px;
        }
        .pb-powered {
          margin-top: 12px;
          font-size: 11px; color: ${muted};
          text-align: end;
        }
        .pb-powered a { color: inherit; }
        .pb-close {
          position: absolute; top: 12px; inset-inline-end: 14px;
          background: none; border: none; cursor: pointer;
          color: ${muted}; font-size: 18px;
          display: ${config.show_close_button ? "block" : "none"};
        }
        .pb-close:focus-visible { outline: 2px solid ${config.primary_color}; outline-offset: 2px; }
        /* Preferences panel */
        .pb-prefs { display: none; }
        .pb-prefs.open { display: block; }
        .pb-category {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 0; border-bottom: 1px solid ${config.text_color}15;
        }
        .pb-category:last-child { border-bottom: none; }
        .pb-cat-label { font-size: 13px; font-weight: 600; }
        .pb-cat-desc { font-size: 11px; color: ${muted}; margin-top: 2px; }
        /* Off-state tracks get a ≥3:1 border so switch state is perceivable
           (the 19%-alpha fill alone was ~1.35:1); knobs get a subtle border
           so they stay visible on light tracks and light primaries. */
        .pb-toggle { appearance: none; width: 40px; height: 22px; border-radius: 11px;
          background: ${config.text_color}30; position: relative; cursor: pointer;
          transition: background 0.2s; flex-shrink: 0;
          border: 1px solid ${mutedBorder};
        }
        .pb-toggle:checked { background: ${config.primary_color}; border-color: ${config.primary_color}; }
        .pb-toggle::after { content: ""; position: absolute; width: 16px; height: 16px;
          border-radius: 50%; background: #fff; top: 2px; inset-inline-start: 2px; transition: inset-inline-start 0.2s;
          border: 1px solid rgba(0,0,0,0.4);
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
        .pb-toggle:checked::after { inset-inline-start: 20px; }
        .pb-toggle:disabled { opacity: 0.5; cursor: not-allowed; }
        /* Vendor list section */
        .pb-vendors { display: none; margin-top: 12px; }
        .pb-vendors.open { display: block; }
        .pb-vendors-title {
          font-size: 13px; font-weight: 700; margin-bottom: 8px;
          padding-bottom: 8px; border-bottom: 1px solid ${config.text_color}15;
        }
        .pb-vendor-group-title {
          font-size: 12px; font-weight: 600; color: ${config.primary_color};
          margin: 10px 0 4px 0;
        }
        .pb-vendor-item {
          display: flex; align-items: center; justify-content: space-between;
          padding: 6px 0; font-size: 12px;
        }
        .pb-vendor-info { flex: 1; min-width: 0; }
        .pb-vendor-name { font-weight: 500; }
        .pb-vendor-link {
          font-size: 10px; color: ${config.primary_color}; text-decoration: none;
          margin-inline-start: 6px;
        }
        .pb-vendor-link:hover { text-decoration: underline; }
        .pb-vendor-purposes { font-size: 11px; color: ${muted}; margin-top: 1px; }
        .pb-vendor-toggle { appearance: none; width: 32px; height: 18px; border-radius: 9px;
          background: ${config.text_color}30; position: relative; cursor: pointer;
          transition: background 0.2s; flex-shrink: 0; margin-inline-start: 8px;
          border: 1px solid ${mutedBorder};
        }
        .pb-vendor-toggle:checked { background: ${config.primary_color}; border-color: ${config.primary_color}; }
        .pb-vendor-toggle::after { content: ""; position: absolute; width: 12px; height: 12px;
          border-radius: 50%; background: #fff; top: 2px; inset-inline-start: 2px; transition: inset-inline-start 0.2s;
          border: 1px solid rgba(0,0,0,0.4);
          box-shadow: 0 1px 2px rgba(0,0,0,0.2);
        }
        .pb-vendor-toggle:checked::after { inset-inline-start: 16px; }
        .pb-vendors-toggle-btn {
          background: none; border: none; cursor: pointer;
          color: ${config.primary_color}; font-size: 12px; font-weight: 600;
          padding: 4px 0; margin-top: 8px;
        }
        .pb-vendors-scroll { max-height: 200px; overflow-y: auto; }
        /* Consent state section */
        .pb-consent-state { margin-bottom: 14px; }
        .pb-state-heading { font-size: 13px; font-weight: 700; margin-bottom: 8px; }
        .pb-state-items { display: flex; flex-direction: column; gap: 4px; margin-bottom: 8px; }
        .pb-state-item { display: flex; align-items: center; gap: 6px; font-size: 13px; }
        .pb-state-icon { width: 16px; height: 16px; flex-shrink: 0; }
        .pb-state-icon.accepted { color: ${config.primary_color}; }
        .pb-state-icon.rejected { color: ${config.text_color}; opacity: 0.5; }
        .pb-details-toggle {
          background: none; border: none; cursor: pointer;
          color: ${config.primary_color}; font-size: 12px; font-weight: 600;
          padding: 4px 0; display: flex; align-items: center; gap: 4px;
        }
        .pb-details-toggle:focus-visible { outline: 2px solid ${config.primary_color}; outline-offset: 2px; }
        .pb-details-chevron { width: 12px; height: 12px; transition: transform 0.2s; }
        .pb-details-chevron.open { transform: rotate(180deg); }
        .pb-consent-details {
          display: none; margin-top: 8px; padding: 12px;
          background: ${config.text_color}08; border-radius: 8px;
          font-size: 12px; line-height: 1.6;
        }
        .pb-consent-details.open { display: block; }
        .pb-detail-label { font-weight: 600; }
        .pb-detail-value { opacity: 0.85; word-break: break-all; }
      </style>

      <div class="pb-overlay" id="pb-overlay"></div>
      <div class="pb-container" role="dialog"${this.isModalLayout() ? ' aria-modal="true"' : ""} aria-label="${copy.title}">
        <button class="pb-close" id="pb-close" aria-label="${copy.close}">✕</button>

        <div class="pb-header">
          ${config.logo_url ? `<img class="pb-logo" src="${this.escapeHtml(config.logo_url)}" alt="Logo" />` : ""}
          <span class="pb-title">${copy.title}</span>
        </div>

        <p class="pb-body">${copy.body}</p>

        ${this.renderConsentState()}

        <!-- Preferences panel (hidden by default) -->
        <div class="pb-prefs" id="pb-prefs" aria-hidden="true">
          <div class="pb-category">
            <div>
              <div class="pb-cat-label">${copy.categories.strictly_necessary}</div>
              <div class="pb-cat-desc">${copy.categoryDescs.strictly_necessary}</div>
            </div>
            <input class="pb-toggle" type="checkbox" checked disabled aria-label="${copy.alwaysActive}" />
          </div>
          ${this.renderCategory("analytics", copy)}
          ${this.renderCategory("advertising", copy)}
          ${this.renderCategory("functional", copy)}
          ${this.renderCategory("social", copy)}
          ${this.renderVendorSection()}
        </div>

        <div class="pb-actions" id="pb-actions">
          <button class="pb-btn pb-btn-accept" id="pb-accept-all">${copy.acceptAll}</button>
          <button class="pb-btn pb-btn-reject" id="pb-reject-all">${copy.rejectAll}</button>
          <button class="pb-btn pb-btn-manage" id="pb-manage">${copy.manage}</button>
        </div>

        ${config.show_powered_by ? `<div class="pb-powered">${copy.poweredBy} <a href="https://beacon.pritect.ai" target="_blank" rel="noopener noreferrer">Pritect Beacon</a></div>` : ""}
      </div>
    `;

    this.bindEvents();
  }

  private renderConsentState(): string {
    if (!this.initialDecisions || !this.consentMeta) return "";
    const copy = this.getCopy();

    const categories = [
      { key: "strictly_necessary", label: copy.categories.strictly_necessary, accepted: true },
      { key: "analytics", label: copy.categories.analytics, accepted: this.initialDecisions.analytics },
      { key: "advertising", label: copy.categories.advertising, accepted: this.initialDecisions.advertising },
      { key: "functional", label: copy.categories.functional, accepted: this.initialDecisions.functional },
      { key: "social", label: copy.categories.social, accepted: this.initialDecisions.social },
    ].filter(c => c.key === "strictly_necessary" || this.config.categories_enabled.includes(c.key));

    const lockSvg = `<svg class="pb-state-icon accepted" viewBox="0 0 20 20" fill="currentColor"><path d="M10 1a4.5 4.5 0 00-4.5 4.5V9H4a1 1 0 00-1 1v8a1 1 0 001 1h12a1 1 0 001-1v-8a1 1 0 00-1-1h-1.5V5.5A4.5 4.5 0 0010 1zm-2.5 8V5.5a2.5 2.5 0 015 0V9h-5z"/></svg>`;
    const xSvg = `<svg class="pb-state-icon rejected" viewBox="0 0 20 20" fill="currentColor"><path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"/></svg>`;

    const stateItems = categories.map(c =>
      `<div class="pb-state-item">${c.accepted ? lockSvg : xSvg}<span>${this.escapeHtml(c.label)}</span></div>`
    ).join("");

    const date = new Date(this.consentMeta.timestamp);
    const formattedDate = date.toLocaleDateString(this.lang, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

    const chevronSvg = `<svg class="pb-details-chevron" id="pb-details-chevron" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/></svg>`;

    return `
      <div class="pb-consent-state">
        <div class="pb-state-heading">${copy.yourCurrentState}</div>
        <div class="pb-state-items">${stateItems}</div>
        <button class="pb-details-toggle" id="pb-details-toggle"><span id="pb-details-label">${copy.showDetails}</span> ${chevronSvg}</button>
        <div class="pb-consent-details" id="pb-consent-details">
          <div><span class="pb-detail-label">${copy.consentDate}:</span><br/><span class="pb-detail-value">${this.escapeHtml(formattedDate)}</span></div>
          <div style="margin-top:6px"><span class="pb-detail-label">${copy.consentId}:</span><br/><span class="pb-detail-value">${this.escapeHtml(this.consentMeta.sessionId)}</span></div>
        </div>
      </div>
    `;
  }

  private renderCategory(
    category: "analytics" | "advertising" | "functional" | "social",
    copy: ReturnType<typeof this.getCopy>
  ): string {
    const enabled = this.config.categories_enabled.includes(category);
    if (!enabled) return "";
    const checked = this.initialDecisions ? this.initialDecisions[category] : false;
    return `
      <div class="pb-category">
        <div>
          <div class="pb-cat-label">${copy.categories[category]}</div>
          <div class="pb-cat-desc">${copy.categoryDescs[category]}</div>
        </div>
        <input class="pb-toggle" type="checkbox" id="pb-cat-${category}" ${checked ? "checked" : ""} aria-label="${copy.categories[category]}" />
      </div>
    `;
  }

  private renderVendorSection(): string {
    if (!this.config.tcf_enabled || !this.vendorList) return "";
    const vendors = this.vendorList.vendors;
    const purposes = this.vendorList.purposes;
    const vendorIds = Object.keys(vendors).sort((a, b) => Number(a) - Number(b));
    if (vendorIds.length === 0) return "";

    // Group vendors by their first declared purpose
    const grouped: Record<number, Vendor[]> = {};
    for (const vid of vendorIds) {
      const v = vendors[vid];
      if (!v) continue;
      const firstPurpose = v.purposes[0] || 0;
      if (!grouped[firstPurpose]) grouped[firstPurpose] = [];
      grouped[firstPurpose].push(v);
    }

    const copy = this.getCopy();
    let html = `
      <button class="pb-vendors-toggle-btn" id="pb-vendors-toggle">${copy.showVendors}</button>
      <div class="pb-vendors" id="pb-vendors">
        <div class="pb-vendors-title">${copy.vendorCount.replace("{count}", String(vendorIds.length))}</div>
        <div class="pb-vendors-scroll">
    `;

    const purposeIds = Object.keys(grouped).map(Number).sort((a, b) => a - b);
    for (const pid of purposeIds) {
      const purposeName = pid > 0 && purposes[String(pid)]
        ? purposes[String(pid)].name
        : "Other";
      html += `<div class="pb-vendor-group-title">${purposeName}</div>`;
      for (const v of grouped[pid]) {
        const purposeNames = v.purposes
          .map((p: number) => purposes[String(p)]?.name)
          .filter(Boolean)
          .join(", ");
        html += `
          <div class="pb-vendor-item">
            <div class="pb-vendor-info">
              <span class="pb-vendor-name">${this.escapeHtml(v.name)}</span>
              ${v.policyUrl ? `<a class="pb-vendor-link" href="${this.escapeHtml(v.policyUrl)}" target="_blank" rel="noopener">${copy.policy}</a>` : ""}
              ${purposeNames ? `<div class="pb-vendor-purposes">${this.escapeHtml(purposeNames)}</div>` : ""}
            </div>
          </div>
        `;
      }
    }

    html += `</div></div>`;
    return html;
  }

  private escapeHtml(str: string): string {
    return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  private bindEvents(): void {
    if (!this.shadow) return;
    const s = this.shadow;

    s.getElementById("pb-accept-all")?.addEventListener("click", () => {
      this.decide({ strictly_necessary: true, analytics: true, advertising: true, functional: true, social: true });
    });

    s.getElementById("pb-reject-all")?.addEventListener("click", () => {
      this.decide({ strictly_necessary: true, analytics: false, advertising: false, functional: false, social: false });
    });

    s.getElementById("pb-manage")?.addEventListener("click", () => {
      const prefsEl = s.getElementById("pb-prefs");
      const actionsEl = s.getElementById("pb-actions");
      if (!prefsEl) return;
      const isOpen = prefsEl.classList.toggle("open");
      prefsEl.setAttribute("aria-hidden", String(!isOpen));
      const manageBtn = s.getElementById("pb-manage");
      if (manageBtn) {
        manageBtn.textContent = isOpen ? this.getCopy().savePrefs : this.getCopy().manage;
        manageBtn.classList.toggle("pb-btn-save", isOpen);
      }

      if (!isOpen) {
        // Save preferences
        const decisions: ConsentDecisions = {
          strictly_necessary: true,
          analytics: (s.getElementById("pb-cat-analytics") as HTMLInputElement)?.checked ?? false,
          advertising: (s.getElementById("pb-cat-advertising") as HTMLInputElement)?.checked ?? false,
          functional: (s.getElementById("pb-cat-functional") as HTMLInputElement)?.checked ?? false,
          social: (s.getElementById("pb-cat-social") as HTMLInputElement)?.checked ?? false,
        };
        this.decide(decisions);
      }
    });

    s.getElementById("pb-close")?.addEventListener("click", () => {
      // Close is a dismissal, not a decision: nothing is persisted and any
      // existing consent is left untouched (matches the "Close" aria-label).
      this.dismiss();
    });

    s.getElementById("pb-overlay")?.addEventListener("click", () => {
      if (this.config.layout === "centered_modal" || this.config.layout === "full_screen") {
        this.dismiss();
      }
    });

    // Focus trap for modal layouts
    if (this.isModalLayout()) {
      s.addEventListener("keydown", (e: Event) => {
        const ke = e as KeyboardEvent;
        if (ke.key !== "Tab") return;
        const focusable = this.getFocusableElements();
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (ke.shiftKey && s.activeElement === first) {
          ke.preventDefault();
          last.focus();
        } else if (!ke.shiftKey && s.activeElement === last) {
          ke.preventDefault();
          first.focus();
        }
      });
    }

    // Escape key dismisses without recording a decision
    s.addEventListener("keydown", (e: Event) => {
      if ((e as KeyboardEvent).key === "Escape") {
        this.dismiss();
      }
    });

    // Consent details toggle
    s.getElementById("pb-details-toggle")?.addEventListener("click", () => {
      const detailsEl = s.getElementById("pb-consent-details");
      const labelEl = s.getElementById("pb-details-label");
      const chevron = s.getElementById("pb-details-chevron");
      if (!detailsEl || !labelEl) return;
      const isOpen = detailsEl.classList.toggle("open");
      chevron?.classList.toggle("open", isOpen);
      const detailsCopy = this.getCopy();
      labelEl.textContent = isOpen ? detailsCopy.hideDetails : detailsCopy.showDetails;
    });

    // Vendor list toggle
    s.getElementById("pb-vendors-toggle")?.addEventListener("click", () => {
      const vendorsEl = s.getElementById("pb-vendors");
      const toggleBtn = s.getElementById("pb-vendors-toggle");
      if (!vendorsEl || !toggleBtn) return;
      const isOpen = vendorsEl.classList.toggle("open");
      const vendorCopy = this.getCopy();
      toggleBtn.textContent = isOpen ? vendorCopy.hideVendors : vendorCopy.showVendors;
    });
  }

  private isModalLayout(): boolean {
    return this.config.layout === "centered_modal" || this.config.layout === "full_screen";
  }

  /**
   * Every currently visible focusable element in the banner, in DOM order.
   * Includes links (powered-by, vendor policies) so the modal focus trap
   * cannot be escaped through them; excludes hidden controls (closed prefs
   * panel, hidden close button). Visibility via getClientRects() because the
   * container is position:fixed (offsetParent would be null).
   */
  private getFocusableElements(): HTMLElement[] {
    if (!this.shadow) return [];
    return Array.from(
      this.shadow.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.getClientRects().length > 0);
  }

  private decide(decisions: ConsentDecisions): void {
    this.onDecision(decisions);
    this.hide();
  }

  /**
   * Dismiss the banner without persisting a decision. Consent state (stored or
   * absent) is left exactly as it was; the caller decides what dismissal means
   * (e.g. session-scoped soft dismiss for the first-show banner, plain close
   * for the preference center).
   */
  private dismiss(): void {
    this.hide();
    this.onDismiss?.();
  }

  private getPositionStyles(layout: BannerLayout): string {
    switch (layout) {
      case "bottom_bar":     return "bottom: 0; inset-inline: 0;";
      case "top_bar":        return "top: 0; inset-inline: 0;";
      case "bottom_left_popup":  return "bottom: 20px; inset-inline-start: 20px;";
      case "bottom_right_popup": return "bottom: 20px; inset-inline-end: 20px;";
      case "centered_modal": return "top: 50%; left: 50%; transform: translate(-50%, -50%);";
      case "full_screen":    return "inset: 0; border-radius: 0; max-width: 100%; width: 100%;";
    }
  }

  private getCopy() {
    const customCopy = this.config.custom_copy as Record<string, Record<string, string>> | null;
    const t = (key: keyof TranslationStrings) => resolveTranslation(key, this.lang, customCopy);
    const isOptOut = this.geo.regulation === "ccpa";

    return {
      title: t("title"),
      body: isOptOut ? t("bodyOptOut") : t("body"),
      acceptAll: t("acceptAll"),
      rejectAll: isOptOut ? t("rejectAllOptOut") : t("rejectAll"),
      manage: t("manage"),
      savePrefs: t("savePrefs"),
      close: t("close"),
      alwaysActive: t("alwaysActive"),
      showVendors: t("showVendors"),
      hideVendors: t("hideVendors"),
      vendorCount: t("vendorCount"),
      policy: t("policy"),
      poweredBy: t("poweredBy"),
      privacySettings: t("privacySettings"),
      yourCurrentState: t("yourCurrentState"),
      showDetails: t("showDetails"),
      hideDetails: t("hideDetails"),
      consentDate: t("consentDate"),
      consentId: t("consentId"),
      categories: {
        strictly_necessary: t("cat_strictly_necessary"),
        analytics: t("cat_analytics"),
        advertising: t("cat_advertising"),
        functional: t("cat_functional"),
        social: t("cat_social"),
      },
      categoryDescs: {
        strictly_necessary: t("catDesc_strictly_necessary"),
        analytics: t("catDesc_analytics"),
        advertising: t("catDesc_advertising"),
        functional: t("catDesc_functional"),
        social: t("catDesc_social"),
      },
    };
  }
}

// Re-export BannerConfig type for use in index.ts
export type { BannerConfig };
